import { mapGetters } from 'vuex';
import { isNil, isEmpty, findLast, last, has, remove, map, clone, pull, forEach } from 'lodash';

import ChartAPI from '@/api/performance/analysis/analysis-chart';
import chartUtil from '@/utils/charts';

/**
 * 성능 분석 자원별 차트 컴포넌트 mixin
 * @type {Object}
 */
export const performanceChart = {
  props: {
    active: {
      type: Boolean,
    },
  },
  data() {
    return {
      mainRscInfoList: Array,
      subRscInfo: Object,
      mainChartVisible: Boolean,
      subChartVisible: false,
      addButtonIconName: 'triangle add',
      subChartButtonClass: ['btn-expand'],
    };
  },
  watch: {
    chartVisible: {
      immediate: true,
      handler(value) {
        this.mainChartVisible = value;
      },
    },
  },
  methods: {
    /**
     * 차트 클릭 시 현재의 crosshair의 point를 읽어와서 다른 자원들의 차트에도 point의 x축 값에 해당하는 crossLine을 그려준다.
     * @author jda
     * @param  {[type]} chart [description]
     * @param  {[type]} e     [description]
     * @return {[type]}       [description]
     */
    onClickChart(chart, e) {
      this.$emit('clickChart', chart, e);
    },
    /**
     * Main Chart의 +, x 버튼 클릭 시 sub Chart가 보여지거나 숨겨진다.
     * @return {[type]} [description]
     * @author jda
     */
    onClickAddButton() {
      if (!isNil(this.addButtonVisible) && !this.addButtonVisible) {
        return;
      }
      this.subChartVisible = !this.subChartVisible;

      if (this.subChartVisible) {
        this.addButtonIconName = 'triangle subtract';
        this.subChartButtonClass = ['btn-expand', 'expand-select'];
        // this.subChartButtonClass = ['btn-expand', 'expand-select'];
        // TODO: Fontawesome5 SVG는 직접 svg요소의 data-icon 변경. 더 좋은 방법이 있으면 수정 필요
        // this.$el.querySelector('svg[data-icon]').setAttribute('data-icon', 'minus');
      } else {
        this.addButtonIconName = 'triangle add';
        this.subChartButtonClass = ['btn-expand'];
        // this.subChartButtonClass = ['btn-expand'];
        // this.$el.querySelector('svg[data-icon]').setAttribute('data-icon', 'plus');
      }

      this.$emit('clickAddButton', this.$vnode.key, !this.subChartVisible);
    },
  },
};

/**
 * 성능 분석 plotLine, plotArea 차트 mixin
 * @type {Object}
 */
export const plotCharts = {
  props: {
    active: {
      type: Boolean,
    },
  },
  data() {
    return {
      legendPoints: [],
      chartRscInfoList: [], // 차트의 자원 정보 배열
    };
  },
  computed: {
    initialPlotLineDatetime() {
      return this.$store.state.performance.initialPlotLineDatetime;
    },
    // jshint ignore:start
    ...mapGetters(['analysisDateParams', 'selectedNode', 'plotLineDatetime', 'isRelativeAnalysisDate']),
    // jshint ignore:end
  },
  watch: {
    active() {
      if (isEmpty(this.objInfo)) {
        this.onShowChart();
      }
    },
    selectedNode() {
      if (isEmpty(this.objInfo)) {
        this.onShowChart();
      }
    },
    /**
     * subchart의 visible 여부에 따라 rscInfo의 데이터를 받아와 차트 series를 바인딩해준다.
     * @return {[type]} [description]
     * @author jda
     */
    visible() {
      this.onShowChart();
    },
    /**
     * 기간필터에서 기간 변경시 차트를 reload한다.
     * @return {Boolean}       [description]
     * @author jda
     */
    analysisDateParams() {
      this.onShowChart();
    },
    /**
     * E2E의 대표 에이전트(objId)를 받아올 때 마다 차트를 reload한다.
     * @return {[type]} [description]
     * @author jda
     */
    objInfo: {
      immediate: true,
      handler() {
        this.onShowChart();
      },
    },
  },
  /**
   * 차트의 Click Event를 넣어준다.
   * @author jda
   * @return {[type]} [description]
   */
  mounted() {
    const me = this;
    this.options.chart.height = 97;

    Object.assign(this.options.plotOptions.series, {
      point: {
        events: {
          mouseOver() {
            me.setCustomTooltip();
          },
        },
      },
      events: {
        mouseOut() {
          if (isNil(me.plotLineDatetime)) {
            me.setCustomTooltip('init');
          } else {
            me.setCustomTooltip('plotLine');
          }
        },
        click(e) {
          me.$emit('clickChart', this.chart, e);
        },
      },
    });
    this.options.chart.events = {
      click(e) {
        me.$emit('clickChart', this, e);
      },
    };
  },
  methods: {
    /**
     * 해당 차트에 x축 값에 맞는 PlotLine을 그려준다.
     * 선택한 datetime의 x값이 맞지 않으면 그 전 값을 그려준다.
     * @author jda
     * @param  {[type]} datetime x축 데이터 값
     */
    setPlotLine(datetime) {
      if (isNil(this.$refs.highchart)) {
        return;
      }

      const chart = this.$refs.highchart.chart;
      if (isNil(chart.series[0]) || isEmpty(chart.series[0].points)) {
        return;
      }
      const findDatetime = datetime || findLast(chart.series[0].points).x;

      this.$store.dispatch('setPlotLineDatetime', findDatetime);
      chart.xAxis[0].update({
        plotLines: [
          {
            color: 'gray',
            width: 1,
            value: findDatetime,
          },
        ],
      });
    },
    /**
     * 차트 옆에 툴팁 겸 범례를 표현한다.
     * 1. plotLineDatetime값이 없을 경우 가장 최근의 데이터를 표현한다.
     * 2. plotLineDatetime이 있을 경우(plotLine이 표현되어 있을 경우) 해당 datetime의 데이터를 표현한다.
     * 3. 그 외의 데이터는 마우스 오버 했을 시 해당 포인트르르 표현한다.
     * @param  {[type]} type [description]
     * @author jda
     */
    setCustomTooltip(type, drawPlotLineFlag) {
      if (isNil(this.$refs.highchart)) {
        return;
      }
      const chart = this.$refs.highchart.chart;
      // chart에 navigator가 있을경우 navigator의 series는 제외한다.
      if (!isEmpty(chart.navigator)) {
        pull(chart.series, chart.navigator.series[0]);
      }

      const points = [];
      if (!isNil(type) && type === 'init') {
        // 첫 로드시 plotLine을 그려준다.
        if (isNil(drawPlotLineFlag) || drawPlotLineFlag) {
          if (isNil(this.initialPlotLineDatetime)) {
            this.setPlotLine();
          } else {
            this.setPlotLine(this.initialPlotLineDatetime);
            this.$store.dispatch('setInitialPlotLineDatetime', null);
          }
        }

        chart.series.forEach((record) => {
          const point = last(record.points);
          if (point) {
            points.push(point);
          }
        });
      } else if (!isNil(type) && type === 'plotLine') {
        const datetime = this.plotLineDatetime;
        chart.series.forEach((record) => {
          const point = findLast(record.points, (xData) => xData.x <= datetime);

          if (point) {
            points.push(point);
          } else {
            // plotLine에 해당하는 point가 없을 경우 가장 최근의 데이터를 범례에 표현한다.
            points.push(last(record.points));
          }
        });
      } else {
        chart.series.forEach((record) => {
          const point = record.searchPoint(event, true);
          if (point) {
            points.push(point);
          } else {
            const finalPoint = findLast(record.points);
            if (!isEmpty(finalPoint)) {
              points.push(finalPoint);
            }
          }
        });
      }
      this.legendPoints = points;
    },
    /**
     * 각 자원별 차트 데이터를 그려준다.
     * @return {[type]} [description]
     * @author jda
     */
    onShowChart() {
      if (this.visible && this.active) {
        const selectedNode = this.selectedNode;
        if (isEmpty(selectedNode) || (!isNil(selectedNode.rscKey) && isEmpty(this.objInfo))) {
          return;
        }

        const params = Object.assign({}, this.analysisDateParams, {
          managetypeId: this.objInfo ? this.objInfo.managetypeId : selectedNode.obj.objManagetypeId,
          isRelative: this.isRelativeAnalysisDate,
        });

        // rsc_key가 TopN의 데이터를 가져오는 파라메터일 경우 rscInfoList에 담지 않는다.
        const rscInfoList = this.rscInfoList;
        if (rscInfoList.length === 1 && !has(rscInfoList[0], 'rscKey')) {
          Object.assign(params, {
            rsctypeId: rscInfoList[0].rsctypeId,
            rscId: rscInfoList[0].rscId,
            objId: selectedNode.obj.objId,
            isTotalCheck: rscInfoList[0].isTotalCheck,
          });
        } else if (!isEmpty(this.objInfo)) {
          // objInfo가 있을 경우, objId가 여러개면 objRscInfoList에 담고 objId가 한 개일 경우 default.
          const objId = this.objInfo.objId.toString();
          const managetypeId = this.objInfo.managetypeId;
          if (objId.split(',').length > 1) {
            const objIds = objId.split(',');
            const objRscInfoList = [];
            objIds.forEach((id) => {
              map(rscInfoList, (record) => {
                const rscInfo = clone(record);
                rscInfo.objId = id;
                rscInfo.managetypeId = managetypeId;
                objRscInfoList.push(rscInfo);
              });
            });
            Object.assign(params, { rscInfoList: objRscInfoList });
          } else {
            Object.assign(params, { objId, rscInfoList });
          }
        } else {
          Object.assign(params, { objId: selectedNode.obj.objId, rscInfoList });
        }

        ChartAPI.getPerformanceChartData(params).then((response) => {
          if (response.data.success) {
            const result = response.data.data;
            this.options.series = result;

            if (!isEmpty(result)) {
              chartUtil.setYAxisRange(this.options);
              const chartRscInfoList = [];
              forEach(result, (item) => {
                chartRscInfoList.push(item.rscInfo);
              });
              this.chartRscInfoList = chartRscInfoList;
            }

            this.$nextTick(() => {
              if (isNil(this.plotLineDatetime)) {
                this.setCustomTooltip('init');
              } else {
                let plotLineDatetime;
                if (isNil(this.initialPlotLineDatetime)) {
                  plotLineDatetime = this.plotLineDatetime;
                } else {
                  plotLineDatetime = this.initialPlotLineDatetime;
                  this.$store.dispatch('setInitialPlotLineDatetime', null);
                }
                this.setPlotLine(plotLineDatetime);
                this.setCustomTooltip('plotLine');
              }
            });

            // 툴팁 단위 표현
            // if (!isNil(result[0].rscUnit)) {
            //   this.options.tooltip.valueSuffix = result[0].rscUnit;
            // }
          }
        });
      } else {
        // 표현되지 않는 차트의 범례 및 툴팁의 데이터 삭제
        remove(this.legendPoints);
      }
    },
    /**
     * 차트의 데이터를 단위변환 한다.
     * @param  {[type]} point [description]
     * @return {[type]}       [description]
     * @author jda
     */
    convertValuByUnit(point) {
      return chartUtil.convertChartValuByUnit(point);
    },
    /**
     * 차트의 시리즈를 삭제한다.
     * @param  {[type]} point [description]
     * @return {[type]}       [description]
     * @author jda
     */
    onDeleteSeries(point) {
      // 시리즈 삭제 시 차트의 시리즈와 범례도 삭제되도록 한다.
      remove(this.legendPoints, point);
      const chart = this.$refs.highchart.chart;
      const deleteSeires = findLast(chart.series, point.series);
      deleteSeires.remove();
    },
    /**
     * 범례 보이기/숨김 처리를 한다.
     * 최대 10개의 항목만 활성화 할 수 있다.
     * @param  {[type]} series [description]
     * @return {[type]}        [description]
     * @author jda
     */
    onClickLegend(currentSeries) {
      chartUtil.clickLegend(this, currentSeries);
    },
    /**
     * 범례 활성화/비활성화의 className을 반환한다.
     * @param  {[type]} currentSeries [description]
     * @return {[type]}               [description]
     * @author jda
     */
    getLegendClass(currentSeries) {
      return chartUtil.getLegendClass(currentSeries);
    },
    /**
     * 범례에 표현할 이름을 반환한다.
     * @param  {[type]} series [description]
     * @return {[type]}        [description]
     * @author jda
     */
    convertLegendName(series) {
      return chartUtil.convertLegendName(series);
    },
    /**
     * 범례의 장비명을 표현한다.
     * @param  {[type]} series [description]
     * @return {[type]}        [description]
     * @author jda
     */
    convertObjName(series) {
      return chartUtil.convertLegendObjName(series);
    },
  },
};



// WEBPACK FOOTER //
// ./src/mixins/performance/charts.js